import { create } from "zustand";

import {
  manualOutline,
  runDemo,
  uploadDemo,
  type DemoRunResult,
  type EdgeMoveInput,
  type ManualOutlineInput,
} from "@/api";

/**
 * One demo run, shared by the three work screens (Eingang → Auslese →
 * Schema) so all three read the same answer instead of each firing its own
 * request off its own case id (design spec 2026-08-12 §3: "die drei
 * Screens lesen denselben Lauf"). A store of its own, not folded into
 * `session`: `session` is who is working and survives the whole visit,
 * this is one run and is thrown away by `reset()` the moment a different
 * drawing starts.
 */
interface DemoRunState {
  /**
   * The Vorgang the current (or last) run belongs to — a persisted UUID from
   * `createCase` (Task 14, design §9), not the demo register's own case id
   * that `start`/`chooseOutline`/`applyEdits` still trade in below. Empty
   * before anything has been created or run.
   *
   * Set explicitly by `adopt` and by `startUpload`'s own `caseId` argument —
   * both entry paths create the Vorgang before they ever call in here, so
   * from Task 14 on nothing overwrites this with a register id the way the
   * old `startUpload(files)` used to (`result.caseId`; still what happens
   * when no Vorgang id is given, for the `/demo` walkthrough this store also
   * serves).
   */
  caseId: string;
  /** The last answer that came back. Survives a failed re-run — see `status`. */
  result: DemoRunResult | null;
  /**
   * Every correction sent with `result`, complete and verbatim — mirrors the
   * backend's own `RunRequest.edits` (spec §6.2: this process holds no
   * session, so the request is the whole record, never a delta). Reset to
   * `[]` wherever the drawn shape changes: a fresh `start`, or a different
   * pick via `chooseOutline`.
   */
  edits: EdgeMoveInput[];
  status: "idle" | "running" | "error";
  /** The failed request's own message. `null` outside `status: "error"`. */
  error: string | null;
  /**
   * The rectangle the Umriss screen starts from, as typed at the Eingang.
   *
   * A handover and not a claim: it seeds the shape, and the two figures become
   * ordinary typed wall lengths from there on. `null` when nobody came that
   * way — the screen then starts from its own default rather than an empty
   * board, because a board with nothing on it has no corner to pull.
   */
  seed: { widthMm: number; depthMm: number } | null;
  /**
   * True while `result` is a worker's reading of a real Vorgang (design
   * 2026-09-23 §3.2) — the schema screen then offers „Übernehmen“ and shows
   * the page the model saw. Every other way into this store sets it false.
   */
  fromExtraction: boolean;

  /**
   * Runs the recorded chain for `caseId` from a clean slate — no `outline`
   * in the request, so the backend may draw one itself where the candidates
   * are unambiguous (spec §2), and no carried-over corrections from
   * whatever case was open before.
   */
  start(caseId: string): Promise<void>;
  /**
   * Runs the dropped drawing — the Eingang's only way in since 12.08.2026.
   *
   * Without `caseId` (unchanged since before Task 14): the case id is not
   * known beforehand and is taken from the answer — the backend says which
   * registered case it recognised, or invents an id for a drawing nobody
   * registered. So it is set on success and not before — a failed drop must
   * not leave a case id standing that was never run.
   *
   * With `caseId` (Task 14): `DropArea.receive()` has already created
   * the Vorgang (`createCase`) and stored the documents on it
   * (`uploadDocuments`) before this runs, so the id is known up front and is
   * what this store keeps — the answer's own `result.caseId` (a register id,
   * or none) never overwrites it, the way it still does when `caseId` is
   * left out.
   */
  startUpload(files: File[], caseId?: string): Promise<void>;
  /** Remembers the rectangle typed at the Eingang, for the Umriss screen to start from. */
  setSeed(seed: { widthMm: number; depthMm: number }): void;
  /**
   * Sends an outline somebody drew by hand and keeps the answer, the same way
   * a read drawing's answer is kept — everything after this point is one run,
   * whoever made the shape (design 2026-08-15 §4.3).
   *
   * No case id is set: nothing was recognised and nothing was recorded, so a
   * later `applyEdits`, which re-runs a *case*, has nothing to re-run. That is
   * a real limit of this path and is stated rather than papered over.
   */
  startManual(outline: ManualOutlineInput): Promise<void>;
  /**
   * Keeps an answer this store did not produce itself — `manualOutline()`,
   * called directly by `OutlineDraftScreen.handOver()` once the drawn outline
   * has become version 1 of a real Vorgang (Task 14, design §9). Not
   * `startManual`: that one calls `manualOutline` itself and sets no case id
   * at all (its own docstring), which is exactly wrong here — `caseId` is
   * known the moment `createCase` returns, and it is what this store keeps
   * from here on, the same way `startUpload`'s own `caseId` argument does for
   * the upload path.
   */
  adopt(caseId: string, result: DemoRunResult): void;
  /** Keeps a worker's reading of `caseId` as the current run. */
  adoptExtraction(caseId: string, result: DemoRunResult): void;
  /**
   * Re-runs the current case with a full, replaced set of corrections,
   * applied to the shape this run actually drew — the system's own pick
   * (`chosenBy: "system"`) counts exactly like a person's (`"user"`); both
   * carry an id in `outlines[].chosen`.
   */
  applyEdits(edits: EdgeMoveInput[]): Promise<void>;
  /**
   * Re-runs the current case against an explicitly picked shape. Corrections
   * made to whatever was drawn before are dropped, not carried to the new
   * shape — a previous drag's wall may not exist in the newly picked
   * arrangement at all.
   */
  chooseOutline(outlineId: string): Promise<void>;
  /** Back to the state before anybody picked a case. */
  reset(): void;
}

type DemoRunData = Pick<
  DemoRunState,
  "caseId" | "result" | "edits" | "status" | "error" | "seed" | "fromExtraction"
>;

const INITIAL_STATE: DemoRunData = {
  caseId: "",
  result: null,
  edits: [],
  status: "idle",
  error: null,
  seed: null,
  fromExtraction: false,
};

/** `runDemo` only ever rejects with a `DemoError` (an `Error`); this stays honest about the rest too. */
function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/**
 * Keep what a drop said about itself across the re-runs a correction causes.
 *
 * `applyEdits` and `chooseOutline` go back through `/demo/run`, which knows a
 * case id and nothing about the file somebody dragged in. Without this, the
 * first correction turns a named drawing back into a case number and drops the
 * recording's date — the screen would quietly stop saying where the reading
 * came from at the exact moment a person started arguing with it.
 */
function withCarriedProvenance(
  next: DemoRunResult,
  previous: DemoRunResult | null,
): DemoRunResult {
  if (!previous || previous.uploadedNames.length === 0) {
    return next;
  }
  return {
    ...next,
    title: previous.title,
    matchedCase: previous.matchedCase,
    source: previous.source,
    uploadedNames: previous.uploadedNames,
    recordedAt: next.recordedAt ?? previous.recordedAt,
  };
}

export const useDemoRun = create<DemoRunState>((set, get) => ({
  ...INITIAL_STATE,

  async start(caseId) {
    set({ caseId, edits: [], status: "running", error: null, fromExtraction: false });
    try {
      const result = await runDemo({ caseId, provider: "replay" });
      set({ result, status: "idle" });
    } catch (cause) {
      set({ status: "error", error: messageOf(cause) });
      throw cause;
    }
  },

  async startUpload(files, caseId) {
    set({
      edits: [],
      status: "running",
      error: null,
      fromExtraction: false,
      // Set up front only when the caller already knows the Vorgang — the
      // no-argument `/demo` walkthrough still learns its case id from the
      // answer, below, exactly as before Task 14.
      ...(caseId !== undefined ? { caseId } : {}),
    });
    try {
      const result = caseId !== undefined
        ? await uploadDemo(files, { caseId })
        : await uploadDemo(files);
      // `caseId ?? result.caseId`, not the other way round: given one, it is
      // the Vorgang this answer was just written onto, and the register id
      // `_upload_pipeline` still resolves independently of it (matched
      // corpus case, or none) must not paper over that.
      set({ result, caseId: caseId ?? result.caseId, status: "idle" });
    } catch (cause) {
      // The previous result stays put, exactly as in `start`: a drop that
      // never arrived is no reason to empty a screen somebody was reading.
      set({ status: "error", error: messageOf(cause) });
      throw cause;
    }
  },

  setSeed(seed) {
    set({ seed });
  },

  async startManual(outline) {
    set({ edits: [], status: "running", error: null, fromExtraction: false });
    try {
      const result = await manualOutline(outline);
      set({ result, caseId: result.caseId, status: "idle" });
    } catch (cause) {
      set({ status: "error", error: messageOf(cause) });
      throw cause;
    }
  },

  adopt(caseId, result) {
    set({ caseId, result, edits: [], status: "idle", error: null, fromExtraction: false });
  },

  adoptExtraction(caseId, result) {
    set({ caseId, result, edits: [], status: "idle", error: null, fromExtraction: true });
  },

  async applyEdits(edits) {
    const { caseId, result } = get();
    // The shape this correction lands on: whichever arrangement this run
    // actually drew, system-chosen or user-chosen alike — `outlines[].chosen`
    // does not distinguish the two, and a drag is valid on either (spec §3).
    const outline = result?.outlines.find((candidate) => candidate.chosen)?.id;
    set({ edits, status: "running", error: null });
    try {
      const next = await runDemo({ caseId, provider: "replay", outline, edits });
      set({ result: withCarriedProvenance(next, result), status: "idle" });
    } catch (cause) {
      set({ status: "error", error: messageOf(cause) });
      throw cause;
    }
  },

  async chooseOutline(outlineId) {
    const { caseId, result: previous } = get();
    set({ edits: [], status: "running", error: null });
    try {
      const result = await runDemo({
        caseId,
        provider: "replay",
        outline: outlineId,
        edits: [],
      });
      set({ result: withCarriedProvenance(result, previous), status: "idle" });
    } catch (cause) {
      set({ status: "error", error: messageOf(cause) });
      throw cause;
    }
  },

  reset() {
    set({ ...INITIAL_STATE });
  },
}));
